import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";

import { CHAT_EMPTY_STATE_HEADING, CHAT_MESSAGES_CONTAINER_CLASS } from "../constants";
import type { ChatMessage, VibeChatStore } from "../stores";
import { MessageRole } from "../types";
import { buildVisibleMessageRows } from "../utils/buildVisibleMessageRows";

import { renderMessageRow, type MessagesVirtuosoContext } from "./MessageRow";
import MessagesVirtuosoFooter from "./MessagesVirtuosoFooter";

const SCROLL_THRESHOLD = 50;
const VIRTUOSO_VIEWPORT_OVERSCAN = 240;
type SupportedScrollBehavior = "auto" | "smooth";
const scrollOnNextFrame = (callback: () => void) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};

interface MessagesScrollContainerProps {
  messages: ChatMessage[];
  isLoading: boolean;
  chatStore: VibeChatStore;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  onRetry?: (messageIndex?: number) => void;
}

const MessagesScrollContainer = observer(
  ({ messages, isLoading, chatStore, containerRef, onRetry }: MessagesScrollContainerProps) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
    const [isUserAtBottom, setIsUserAtBottom] = useState(true);
    const { currentChatId } = chatStore;
    const lastHistoryScrollChatId = useRef<string | null>(null);
    const shouldFollowOutputRef = useRef(true);

    const visibleRows = useMemo(
      () => buildVisibleMessageRows(messages, isLoading),
      [messages, isLoading],
    );
    const virtuosoContext = useMemo<MessagesVirtuosoContext>(
      () => ({ isLoading, chatStore, onRetry }),
      [chatStore, isLoading, onRetry],
    );
    const lastMessage = messages[messages.length - 1];
    const lastMessageContent = lastMessage?.content ?? "";

    const scrollToBottom = useCallback(
      (behavior: ScrollBehavior) => {
        if (!scrollParent) {
          return;
        }

        scrollParent.scrollTo({
          top: scrollParent.scrollHeight,
          behavior,
        });
      },
      [scrollParent],
    );

    const scrollVirtuosoToBottom = useCallback(
      (behavior: SupportedScrollBehavior) => {
        if (visibleRows.length === 0) {
          return;
        }

        virtuosoRef.current?.scrollToIndex({
          index: visibleRows.length - 1,
          align: "end",
          behavior,
        });
        scrollOnNextFrame(() => scrollToBottom(behavior));
      },
      [scrollToBottom, visibleRows.length],
    );

    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        setScrollParent(node);
      },
      [containerRef],
    );

    const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
      if (atBottom) {
        shouldFollowOutputRef.current = true;
      }
      setIsUserAtBottom(atBottom);
    }, []);

    const handleWheelCapture = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
      if (event.deltaY < 0) {
        shouldFollowOutputRef.current = false;
        setIsUserAtBottom(false);
      }
    }, []);

    const prevMessagesLength = useRef(messages.length);
    useEffect(() => {
      const shouldScrollLoadedHistory =
        Boolean(currentChatId) &&
        lastHistoryScrollChatId.current !== currentChatId &&
        visibleRows.length > 0 &&
        Boolean(scrollParent);

      if (!shouldScrollLoadedHistory) {
        return;
      }

      lastHistoryScrollChatId.current = currentChatId;
      setIsUserAtBottom(true);
      scrollOnNextFrame(() => {
        scrollVirtuosoToBottom("auto");
      });
    }, [currentChatId, messages.length, scrollParent, scrollVirtuosoToBottom, visibleRows.length]);

    useEffect(() => {
      const newestMessage = messages[messages.length - 1];
      if (
        messages.length > prevMessagesLength.current &&
        newestMessage?.role === MessageRole.USER
      ) {
        shouldFollowOutputRef.current = true;
        setIsUserAtBottom(true);
        scrollOnNextFrame(() => scrollVirtuosoToBottom("smooth"));
      }
      prevMessagesLength.current = messages.length;
    }, [messages, scrollVirtuosoToBottom]);

    useEffect(() => {
      if (!shouldFollowOutputRef.current || !scrollParent || visibleRows.length === 0) {
        return;
      }

      const frame = requestAnimationFrame(() => {
        scrollVirtuosoToBottom("smooth");
      });

      return () => cancelAnimationFrame(frame);
    }, [isLoading, lastMessageContent, scrollParent, scrollVirtuosoToBottom, visibleRows.length]);

    return (
      <div
        ref={setContainerRef}
        className={CHAT_MESSAGES_CONTAINER_CLASS}
        style={{ overflowAnchor: "none" }}
        onWheelCapture={handleWheelCapture}
      >
        {messages.length === 0 && !isLoading && (
          <div className="flex h-full flex-col items-center justify-center">
            <h3 className="mb-3 text-lg font-medium text-gray-700">{CHAT_EMPTY_STATE_HEADING}</h3>
          </div>
        )}

        {messages.length > 0 && scrollParent && (
          <div className="mx-auto w-full max-w-2xl">
            <Virtuoso
              ref={virtuosoRef}
              customScrollParent={scrollParent}
              data={visibleRows}
              context={virtuosoContext}
              computeItemKey={(_, row) => row.key}
              itemContent={renderMessageRow}
              components={{ Footer: MessagesVirtuosoFooter }}
              followOutput={isUserAtBottom ? "smooth" : false}
              atBottomThreshold={SCROLL_THRESHOLD}
              atBottomStateChange={handleAtBottomStateChange}
              increaseViewportBy={{
                top: VIRTUOSO_VIEWPORT_OVERSCAN,
                bottom: VIRTUOSO_VIEWPORT_OVERSCAN,
              }}
            />
          </div>
        )}
      </div>
    );
  },
);

export default MessagesScrollContainer;
